import { API } from '@/core/Api';
import { getVerifiedToken } from '@/core/U';
import { LLM_MODEL_TABLE } from '@/types/Chat';
import { LLMModel } from '@/types/Enum';
interface StreamHandler {
  onTextChunk?: (text: string) => void;
  onImageGenerated?: (images: any[]) => void;
  onImageGenerationStarted?: () => void;
  onReasoningUpdate?: (reasoning: any) => void;
  onMessageUpdate?: (message: any) => void;
  onComplete?: (finalResult: StreamResult) => void;
  onError?: (error: Error) => void;
}
//Streaming result
interface StreamResult {
  text: string;
  images: any[];
  reasoning?: any[];
  messages?: any[];
  rawResponse?: any;
}
export class UnifiedLLM {
  /**
   * OpenAI API 스트리밍 응답을 처리하는 공통 함수
   * @param endpoint API 엔드포인트 (상대 경로)
   * @param requestData 요청 데이터
   * @param handlers 스트리밍 이벤트 핸들러들
   * @param abortSignal 취소 신호 (옵셔널)
   * @returns 최종 스트리밍 결과
   */
  private async streamAiResponse(
    endpoint: string,
    requestData: any,
    handlers: StreamHandler,
    abortSignal?: AbortSignal, // AbortCtrl 추가
  ): Promise<StreamResult> {
    //token 만료시 handle
    const token = await getVerifiedToken();
    let accumulatedText = '';
    const allImages: any[] = [];
    const allReasoning: any[] = [];
    const allMessages: any[] = [];
    let rawResponse: any = null;

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_SERVER}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ ...requestData, stream: true }),
        signal: abortSignal, // fetch에 signal 전달
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenAI API 오류: ${response.statusText} - ${errorText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Response body is null');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        // 취소 확인
        if (abortSignal?.aborted) {
          reader.cancel();
          throw new Error('Stream aborted by user');
        }

        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const lines = buffer.split('\n');

        buffer = lines.pop() || '';

        for (const line of lines) {
          // 취소 확인 (루프 내부에서도)
          if (abortSignal?.aborted) {
            reader.cancel();
            throw new Error('Stream aborted by user');
          }

          const trimmedLine = line.trim();

          if (trimmedLine.startsWith('event: ')) {
            continue;
          }

          if (trimmedLine.startsWith('data: ')) {
            const data = trimmedLine.slice(6).trim();

            if (data === '[DONE]') {
              const finalResult: StreamResult = {
                text: accumulatedText,
                images: allImages,
                reasoning: allReasoning,
                messages: allMessages,
                rawResponse,
              };
              handlers.onComplete?.(finalResult);
              return finalResult;
            }

            if (!data) continue;

            try {
              const parsed = JSON.parse(data);
              rawResponse = parsed;
              // 텍스트 콘텐츠 처리
              if (parsed.choices?.[0]?.delta?.content) {
                const content = parsed.choices[0].delta.content;
                accumulatedText += content;
                handlers.onTextChunk?.(content);
              }

              // 이미지 생성 결과 처리
              if (parsed.output) {
                //이벤트 시작 감지 -> 이미지 생성
                const imageInProgressOutputs = parsed.output.filter(
                  (item: any) =>
                    item.type === 'image_generation_call' && item.status === 'in_progress',
                );

                if (imageInProgressOutputs.length > 0) {
                  handlers.onImageGenerationStarted?.();
                }

                // 이미지 생성 완료 처리 (status: "completed")
                const imageOutputs = parsed.output.filter(
                  (item: any) =>
                    item.type === 'image_generation_call' && item.status === 'completed',
                );

                if (imageOutputs.length > 0) {
                  const processedImages = imageOutputs
                    .map((imageData: any) => ({
                      ...imageData,
                      dataUrl: imageData.result
                        ? `data:image/${imageData.output_format || 'png'};base64,${
                            imageData.result
                          }`
                        : null,
                    }))
                    .filter((img: any) => img.dataUrl);

                  // 중복 이미지 방지: 이미 존재하는 ID는 제외
                  const newImages = processedImages.filter(
                    (newImg: any) =>
                      !allImages.some((existingImg: any) => existingImg.id === newImg.id),
                  );

                  if (newImages.length > 0) {
                    console.log(
                      `🎨 Adding ${newImages.length} new images (filtered ${
                        processedImages.length - newImages.length
                      } duplicates)`,
                    );
                    allImages.push(...newImages);
                    handlers.onImageGenerated?.(newImages);
                  }
                }

                // 추론 데이터 처리
                const reasoningOutputs = parsed.output.filter(
                  (item: any) => item.type === 'reasoning',
                );
                if (reasoningOutputs.length > 0) {
                  allReasoning.push(...reasoningOutputs);
                  handlers.onReasoningUpdate?.(reasoningOutputs);
                }

                // 메시지 데이터 처리
                const messageOutputs = parsed.output.filter((item: any) => item.type === 'message');
                if (messageOutputs.length > 0) {
                  allMessages.push(...messageOutputs);
                  handlers.onMessageUpdate?.(messageOutputs);

                  // 메시지에서 텍스트 콘텐츠 추출
                  messageOutputs.forEach((msg: any) => {
                    if (msg.content && Array.isArray(msg.content)) {
                      msg.content.forEach((contentItem: any) => {
                        if (contentItem.type === 'output_text' && contentItem.text) {
                          if (accumulatedText.length === 0) {
                            accumulatedText += contentItem.text;
                            handlers.onTextChunk?.(contentItem.text);
                          }
                        }
                      });
                    }
                  });
                }
              }

              // finish_reason 체크
              const finishReason = parsed.choices?.[0]?.finish_reason;
              if (finishReason) {
                const finalResult: StreamResult = {
                  text: accumulatedText,
                  images: allImages,
                  reasoning: allReasoning,
                  messages: allMessages,
                  rawResponse: parsed,
                };
                handlers.onComplete?.(finalResult);
                return finalResult;
              }
            } catch (error) {
              // JSON 파싱 에러가 발생해도 스트리밍 계속 진행
              if (data.length > 100) {
                console.warn('JSON parse error for large data chunk:', error);
              } else {
                console.warn('JSON parse error:', error, 'for data:', data);
              }
            }
          }
        }
      }

      reader.releaseLock();
      // 스트리밍 완료 후 최종 결과 반환
      const finalResult: StreamResult = {
        text: accumulatedText,
        images: allImages,
        reasoning: allReasoning,
        messages: allMessages,
        rawResponse,
      };
      handlers.onComplete?.(finalResult);
      return finalResult;
    } catch (error) {
      // AbortError 처리
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Stream was aborted');
        const abortResult: StreamResult = {
          text: accumulatedText,
          images: allImages,
          reasoning: allReasoning,
          messages: allMessages,
          rawResponse,
        };
        return abortResult; // 에러를 던지지 않고 현재까지의 결과 반환
      }

      const errorObj = error instanceof Error ? error : new Error(String(error));
      handlers.onError?.(errorObj);
      throw errorObj;
    }
  }

  /**
   * Gemini 비전 스트리밍 - 이미지 생성기능도 존재
   * @param llm 모델
   * @param messages 질문 내용
   * @param onChunk 스트리밍 콜백
   * @param options 추가 옵션들
   */
  async generateWithLlmStreaming(
    llm: LLMModel,
    messages: any[],
    onChunk?: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      model?: string;
      onImageGenerated?: (imageData: any[]) => void;
      onImageGenerationStarted?: () => void;
      thinkingMode?: boolean;
      abortSignal?: AbortSignal; // 추가
      image_generation_required?: boolean;
    } = {},
  ): Promise<StreamResult> {
    const detailModel = LLM_MODEL_TABLE[llm];
    const requestData = {
      model: options.model ?? detailModel,
      messages: messages,
      max_tokens: options.max_tokens || 4000,
      temperature: options.temperature || 1.0,
      thinkingMode: options.thinkingMode,
      //thinking -> gpt reasoningEffort ('low, medium, high) & gemini -> thinkingMode & enableThinking -> sonnet
      stream: true,
      image_generation_required: options.image_generation_required ?? false,
    };

    const endpoint =
      llm === LLMModel.Claude_Sonnet_4_0
        ? '/api/ai/anthropic/chat/completions'
        : llm === LLMModel.GPT_5
        ? '/api/ai/openai/chat/completions'
        : '/api/ai/gemini/chat/completions';
    return this.streamAiResponse(
      endpoint,
      requestData,
      {
        onTextChunk: onChunk,
        onImageGenerated: options.onImageGenerated,
        onImageGenerationStarted: options.onImageGenerationStarted,
        onComplete: (result) => {
          console.log('✅ Streaming completed. Final results:', {
            text: result.text,
            images: result.images,
          });
        },
        onError: (error) => {
          console.error('OpenAI Vision Streaming error:', error);
          throw error;
        },
      },
      options.abortSignal, // signal 전달
    );
  }
}
